import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastManager } from '@porsche-design-system/components-angular';
import { environment } from '../../../../../environments/environment';
import { COMMON_MESSAGES } from '../../../../core/constants/app.constants';
import { AuthResponse } from '../../../../core/models/auth.model';
import { getErrorMessage } from '../../../../core/models/error.model';
import { AuthUserService } from '../../../../core/services/auth-user.service';
import { LoggingService } from '../../../../core/services/logging.service';
import { SHARED_IMPORTS } from '../../../../shared/shared-imports';
import { emailFormatValidator } from '../../../../shared/utils/form-validation.util';
import { AuthorizationService } from '../../services/authorization.service';
import { LoginService } from '../../services/login.service';

/** Typed controls of the login form. */
interface LoginControls {
  userName: FormControl<string>;
  password: FormControl<string>;
}

/**
 * AuthorizationComponent (PPN authorization + login)
 *
 * Single entry component at `/ppn-auth`:
 * - `environment.isDev === false` (UAT / production): authorizes through PPN on init
 *   (`GET /users/authorize`) and forwards to the landing page of the user's role;
 *   failures are shown as a toast.
 * - `environment.isDev === true` (local / dev): shows the e-mail / password login form
 *   (`POST /users/authenticate`) instead.
 *
 * Notes:
 * - Avoid logging PII (login id, e-mail); logs go through `LoggingService`.
 * - The app is zoneless, so values read by the template after async work are signals.
 */
@Component({
  selector: 'app-authorization',
  imports: [...SHARED_IMPORTS],
  templateUrl: './authorization.html',
  styleUrl: './authorization.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthorizationComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly authUserService = inject(AuthUserService);
  private readonly toastManager = inject(ToastManager);
  private readonly logger = inject(LoggingService);

  /** `true` in dev environments: the login form is shown instead of PPN authorization. */
  readonly isDev = environment.isDev;
  /** Reactive form with the login credentials (typed). */
  readonly loginForm: FormGroup<LoginControls> = this.formBuilder.nonNullable.group({
    userName: ['', [Validators.required, emailFormatValidator()]],
    password: ['', [Validators.required]],
  });
  /** Whether a submit was attempted; reveals the validation messages. */
  isSubmitted = false;
  /** `true` while the login request runs. */
  readonly isLoading = signal(false);

  /**
   * Starts from a clean session; in non-dev environments authorizes through PPN.
   *
   * @returns void
   */
  ngOnInit(): void {
    // DEBUG: verify which environment is loaded at runtime (suppressed in UAT / prod)
    this.logger.debug('[AuthorizationComponent] environment', environment);
    this.logger.debug('[AuthorizationComponent] isDev:', this.isDev);
    this.authUserService.clearSession();
    if (!this.isDev) this.authorizeWithPpn();
  }

  /**
   * Provides easy access to the login form's controls for template binding.
   *
   * @returns The `controls` of the `loginForm`.
   */
  get userForm(): LoginControls {
    return this.loginForm.controls;
  }

  /**
   * Trims leading/trailing whitespace from a login control without emitting events.
   *
   * @param key Name of the control to trim.
   * @returns void
   */
  trim(key: 'userName' | 'password'): void {
    const control = this.loginForm.controls[key];
    const trimmed = control.value.trim();
    if (trimmed !== control.value) control.patchValue(trimmed, { emitEvent: false });
  }

  /**
   * Validates the login form and authenticates the user (dev only).
   *
   * Flow:
   * - Returns early when the form is invalid (messages become visible).
   * - Calls {@link LoginService.authenticate}; on success stores the session and navigates
   *   to the landing page of the role, on error shows the message as a toast.
   *
   * @returns void
   */
  onSubmit(): void {
    this.isSubmitted = true;
    if (this.loginForm.invalid || this.isLoading()) return;

    this.isLoading.set(true);
    const { userName, password } = this.loginForm.getRawValue();
    this.loginService.authenticate({ userName: userName.trim(), password }).subscribe({
      next: (res) => this.completeLogin(res),
      error: (error: unknown) => {
        this.logger.error('AuthorizationComponent.onSubmit: HTTP error', error);
        this.showError(error);
      },
    });
  }

  /**
   * Calls the PPN authorize endpoint and starts the session (UAT / production).
   *
   * @returns void
   */
  private authorizeWithPpn(): void {
    this.authorizationService.authorize().subscribe({
      next: (res) => this.completeLogin(res),
      error: (error: unknown) => {
        this.logger.error('AuthorizationComponent.authorizeWithPpn: HTTP error', error);
        this.showError(error);
      },
    });
  }

  /**
   * Stops the login progress indicator and shows the failure as a toast.
   *
   * @param error Error returned by the backend.
   * @returns void
   */
  private showError(error: unknown): void {
    this.isLoading.set(false);
    this.toastManager.addMessage({ text: getErrorMessage(error), state: 'error' });
  }

  /**
   * Stores the session and forwards to the landing page of the user's role.
   *
   * @param result Token and user returned by the backend.
   * @returns void
   */
  private completeLogin(result: AuthResponse | undefined): void {
    this.isLoading.set(false);
    if (!result?.token) {
      this.toastManager.addMessage({ text: COMMON_MESSAGES.SOMETHING_WENT_WRONG, state: 'info' });
      return;
    }
    this.authUserService.startSession(result.token, result.user);
    this.toastManager.addMessage({ text: COMMON_MESSAGES.LOGIN_SUCCESS, state: 'success' });
    const home = this.authUserService.getHomeRoute();
    if (!home) {
      this.logger.error(
        'AuthorizationComponent.completeLogin: role has no permitted landing route',
      );
      this.toastManager.addMessage({ text: COMMON_MESSAGES['401_MESSAGE'], state: 'error' });
      return;
    }
    void this.router
      .navigate([home], { replaceUrl: true })
      .then((navigated) => {
        if (!navigated) {
          this.logger.error(
            'AuthorizationComponent.completeLogin: router rejected the landing route',
            undefined,
            { route: home },
          );
        }
      })
      .catch((error: unknown) =>
        this.logger.error('AuthorizationComponent.completeLogin: navigation failed', error),
      );
  }
}
